import * as RT from '@radix-ui/react-tooltip';
import { forwardRef, type HTMLAttributes, type ReactNode } from 'react';
import { cn } from './cn';

// Extends span attributes so that when Tooltip is used as an `asChild` trigger child (e.g. inside a
// Popconfirm/Popover), the ref + onClick/data-*/aria-* those parents inject reach the trigger DOM node.
export interface TooltipProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'title'> {
  label?: ReactNode;
  /** antd alias for `label`. */
  title?: ReactNode;
  children: ReactNode;
  side?: 'top' | 'bottom' | 'left' | 'right';
  /** antd placement (top/bottom/left/right + corner variants) — mapped to a side. */
  placement?: string;
  className?: string;
  /** Hover delay before showing (ms). */
  delay?: number;
  /** Make the trigger fill its container (block, full width) instead of shrink-wrapping —
   *  needed when the tooltip wraps full-width content like a progress bar in a table cell. */
  block?: boolean;
  /**
   * Render the trigger as plain INLINE content rather than an inline-flex box.
   *
   * `text-overflow: ellipsis` only ellipsizes inline content, so the default inline-flex trigger
   * silently defeats an ancestor that truncates: a long value inside a `<div class="truncate">`
   * table cell got hard-clipped mid-character with no "…". Use this whenever a tooltip wraps text
   * that an ancestor is expected to truncate.
   */
  inline?: boolean;
}

const SIDE_OF: Record<string, 'top' | 'bottom' | 'left' | 'right'> = {
  top: 'top', topLeft: 'top', topRight: 'top',
  bottom: 'bottom', bottomLeft: 'bottom', bottomRight: 'bottom',
  left: 'left', leftTop: 'left', leftBottom: 'left',
  right: 'right', rightTop: 'right', rightBottom: 'right',
};

/**
 * Hover / focus tooltip (replaces antd Tooltip). Built on Radix Tooltip: it PORTALS
 * to the body and is collision-aware (auto-shifts/flips to stay on screen), so it
 * never gets clipped by an `overflow` scroll container or the viewport edge — e.g. a
 * delete button at the right edge of a table row. Accepts `label` (or antd `title`).
 */
export const Tooltip = forwardRef<HTMLSpanElement, TooltipProps>(function Tooltip(
  // `...rest` captures any props a Radix `asChild` parent (Popconfirm/Popover) injects — onClick, ref,
  // data-state, aria-* — so they reach the trigger span instead of being silently dropped (which would
  // leave that parent's popup unanchored → a dead click). forwardRef is required for the same reason.
  { label, title, children, side = 'top', placement, className, delay = 150, block, inline, ...rest }, ref,
) {
  const content = label ?? title;
  if (content == null || content === '') return <>{children}</>;
  const resolvedSide = placement ? (SIDE_OF[placement] ?? 'top') : side;
  return (
    <RT.Provider delayDuration={delay} disableHoverableContent>
      <RT.Root>
        <RT.Trigger asChild>
          <span ref={ref} className={block ? 'block w-full' : inline ? 'inline' : 'inline-flex'} {...rest}>{children}</span>
        </RT.Trigger>
        <RT.Portal>
          <RT.Content
            side={resolvedSide}
            sideOffset={6}
            collisionPadding={8}
            className={cn(
              'z-[var(--z-tooltip)] max-w-xs rounded-md px-2.5 py-1.5 type-label leading-snug text-white shadow-pop',
              // Light: a dark chip on the light page. Dark: a clearly ELEVATED slate (well above every
              // surface) with a hairline ring — the old fixed #1f2a22 was nearly the same value as the
              // dark page, so the label was invisible on hover.
              'bg-[#1f2a22] dark:bg-[#3a453c] dark:ring-1 dark:ring-white/12',
              // `pointer-events-none` is load-bearing, not cosmetic. A tooltip is positioned over its own
              // trigger's surroundings, and with pointer events ON it SWALLOWS CLICKS aimed at whatever it
              // overlaps. Measured: a row's tooltip covered y335-364 while the confirmation button beneath
              // it sat at y319-351, so `elementFromPoint` at the button's centre returned the tooltip and the
              // click never reached "Cancel transfer". The same Tooltip(Popconfirm(button)) pattern is used
              // on the orders list and detail, expense reports, advance requests, regularization and the
              // coverage card, so this one line fixes a dead confirmation on seven pages.
              // A tooltip is never a click target, so nothing legitimate needs the events.
              'pointer-events-none',
              'select-none data-[state=delayed-open]:animate-fade-in data-[state=instant-open]:animate-fade-in',
              className,
            )}
          >
            {content}
            <RT.Arrow className="fill-[#1f2a22] dark:fill-[#3a453c]" width={11} height={5} />
          </RT.Content>
        </RT.Portal>
      </RT.Root>
    </RT.Provider>
  );
});
